import { get } from "lodash";

import type { Fields, Indexed, Paths } from "./types";

export type ScalarOrVector<T> = T | T[] | Set<T>;

export function toVector<T>(values?: ScalarOrVector<T>): T[] {
  if (!values) {
    return [];
  }
  if (values instanceof Set) {
    return [...values];
  }
  if (Array.isArray(values)) {
    return values;
  }
  return [values];
}

export function getInitials(name: string) {
  return name
    .split(/\W/g)
    .map((s) => s[0] ?? "")
    .join("");
}

export function getUniqueName(defaultName: string, nameList: string[]) {
  const nameSet = new Set(nameList);

  let currentIndex = 1;
  let currentName = defaultName;

  while (nameSet.has(currentName)) {
    currentName = `${defaultName} (${currentIndex})`;
    currentIndex++;
  }

  return currentName;
}

export type Getter<Shape, Value extends string = string> = <Item extends Shape>(
  item: Item,
) => Value;
export type FieldOrGetter<Shape, Value extends string = string> =
  | Fields<Shape, Value>
  | Getter<Shape, Value>;
export type PathOrGetter<Shape, Value extends string = string> =
  | Paths<Shape, Value>
  | FieldOrGetter<Shape, Value>;

export function getPathGetter<Shape, Value extends string = string>(
  pathOrGetter: PathOrGetter<Shape, Value>,
) {
  return pathOrGetter instanceof Function
    ? pathOrGetter
    : (item: Shape) => get(item, pathOrGetter) as any as Value;
}

export function getLookupMap<Shape, Value extends string = string>(
  arr: readonly Shape[],
  fieldOrGetter: FieldOrGetter<Shape, Value>,
) {
  const getId = getPathGetter(fieldOrGetter);

  return arr.reduce<Partial<Record<string, Shape>>>((map, item) => {
    map[getId(item)] = item;
    return map;
  }, {});
}

export function getArrayMap<Shape, Value extends string = string>(
  arr: readonly Shape[],
  fieldOrGetter: FieldOrGetter<Shape, Value>,
) {
  const getId = getPathGetter(fieldOrGetter);

  return arr.reduce<Partial<Record<Value, Shape[]>>>((acc, item) => {
    const id = getId(item);
    (acc[id] ??= []).push(item);
    return acc;
  }, {});
}

const identity = <Shape>(item: Shape) => item;

export function getUniqueArray<Shape extends string>(arr: Shape[]): Shape[];
export function getUniqueArray<Shape>(
  arr: Shape[],
  fieldOrGetter: FieldOrGetter<Shape>,
): Shape[];
export function getUniqueArray<Shape>(
  arr: Shape[],
  fieldOrGetter?: FieldOrGetter<Shape>,
): Shape[] {
  return Object.values(
    getLookupMap(arr, fieldOrGetter ?? (identity as any)),
  ) as Shape[];
}

export const isNonNegativeInt = (n: number) =>
  Number.isNaN(n) || (Number.isInteger(n) && n >= 0);

export function sleep(ms: number) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

export function addIndex<ItemShape>(
  item: ItemShape,
  index: number,
): Indexed<ItemShape> {
  return { ...item, index };
}
